import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Segmented, Avatar, Button } from 'antd';
import {
  TeamOutlined, LoginOutlined, LogoutOutlined, ClockCircleOutlined,
  EnvironmentOutlined, CheckCircleOutlined, ApartmentOutlined, UserOutlined, EyeOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import SearchBar from '../searchBar';
import ColumnVisibility from '../columnVisibility';
import CommonTable from '../commonTable';
import CustomPagination from '../pagination';
import DateRangeFilter from '../dateRangeFilter';
import StatCard from '../dashboards/StatCard';
import { useGetAttendanceQuery } from '../../services/attendance';
import { ATTENDANCE_FLOORS } from '../../constants/attendanceFloors';
import { AttendanceDetailRoute } from '../../routes/routepath';
import './styles.scss';

/* ─── Types ──────────────────────────────────────────────────────────── */

interface AttendancePageProps {
  userType: string;
  title:    string;
  userId?:  string;
  extra?:   React.ReactNode;
  // Adds the "All Floors / Reception / Cardio / …" filter next to the status
  // tabs — only Member Attendance needs it, so it stays off for other roles.
  showFloorFilter?: boolean;
}

type VisibleColumns = Record<string, boolean>;

interface Session {
  key:        string;
  attendeeId: string | null;   // the person this row belongs to — for the detail API
  date:       string | null;   // day this row covers — for the detail API
  name:       string;
  mobile:     string;
  branch:     string;
  floors:     string[];
  floor:      string;
  checkIn:    string | null;   // raw timestamp
  checkOut:   string | null;
  durationMin: number | null;  // null while ongoing
  status:     'inside' | 'left' | 'out';
  sortTime:   number;
}

/* ─── Helpers ────────────────────────────────────────────────────────── */

const initials = (n: string) =>
  n && n !== '—' ? n.trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase() : '?';
const AV_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ec4899', '#06b6d4', '#8b5cf6'];
const avatarColor = (n: string) => AV_COLORS[(n?.length || 0) % AV_COLORS.length];

const fmtTime = (t: string | null) => (t ? dayjs(t).format('hh:mm A') : null);
const fmtDate = (s: { date?: string | null; checkIn: string | null; checkOut: string | null }) => {
  const d = s.date || s.checkIn || s.checkOut;
  return d && dayjs(d).isValid() ? dayjs(d).format('DD MMM YYYY') : null;
};
const fmtDur  = (m: number) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);

/* ─── Map server's pre-aggregated attendance rows into sessions ──────── */
// The API already pairs punches per member/day, so each record is one row:
// { member, firstCheckIn, lastCheckOut, durationMinutes, floors[], status }.

function buildSessions(records: any[]): Session[] {
  const sessions: Session[] = records.map((r, i) => {
    const inT  = r.firstCheckIn ?? null;
    const outT = r.lastCheckOut ?? null;
    const rawStatus = (r.status || '').toString().toLowerCase();
    const status: Session['status'] =
      rawStatus === 'inside' ? 'inside'
        : (!inT && outT ? 'out' : 'left');
    const floors: string[] = Array.isArray(r.floors) ? r.floors.filter(Boolean) : (r.floors ? [r.floors] : []);
    const attendeeId = r.userId ?? r.biometricUserId ?? null;
    return {
      key:        `${attendeeId ?? 'row'}-${r.date ?? ''}-${i}`,
      attendeeId,
      date:       r.date ?? null,
      name:       r.member || r.name || '—',
      mobile:     r.userTypeLabel || (r.biometricUserId ? `ID ${r.biometricUserId}` : ''),
      branch:     r.branchName || '—',
      floors,
      floor:      floors.join(', '),
      checkIn:    inT,
      checkOut:   outT,
      durationMin: typeof r.durationMinutes === 'number' ? r.durationMinutes : null,
      status,
      sortTime:   new Date(outT || inT || 0).getTime(),
    };
  });

  return sessions.sort((a, b) => b.sortTime - a.sortTime);
}

/* ─── Columns ────────────────────────────────────────────────────────── */

const buildColumns = (hasCheckOut: boolean, onViewAttendance: (s: Session) => void) => [
  {
    title: 'Member', key: 'name', width: 210,
    render: (_: unknown, s: Session) => (
      <div className="att-member">
        <Avatar style={{ background: `${avatarColor(s.name)}22`, color: avatarColor(s.name), fontWeight: 700 }}>
          {initials(s.name)}
        </Avatar>
        <div>
          <div className="att-member-name">{s.name}</div>
          <div className="att-member-sub">{s.mobile}</div>
        </div>
      </div>
    ),
  },
  {
    title: 'Date', key: 'date', width: 120,
    render: (_: unknown, s: Session) => {
      const d = fmtDate(s);
      return d ? <span className="att-date">{d}</span> : <span className="att-time none">—</span>;
    },
  },
  {
    title: 'Check-in', key: 'checkIn', width: 120,
    render: (_: unknown, s: Session) =>
      s.checkIn ? <span className="att-time in">↓ {fmtTime(s.checkIn)}</span> : <span className="att-time none">—</span>,
  },
  ...(hasCheckOut ? [
    {
      title: 'Check-out', key: 'checkOut', width: 120,
      render: (_: unknown, s: Session) =>
        s.checkOut ? <span className="att-time out">↑ {fmtTime(s.checkOut)}</span> : <span className="att-time none">—</span>,
    },
    {
      title: 'Duration', key: 'duration', width: 130,
      render: (_: unknown, s: Session) => {
        if (s.status === 'inside' && s.checkIn) {
          const mins = Math.max(0, dayjs().diff(dayjs(s.checkIn), 'minute'));
          return <span className="att-dur ongoing">{fmtDur(mins)} · live</span>;
        }
        return s.durationMin != null ? <strong className="att-dur">{fmtDur(s.durationMin)}</strong> : <span className="att-time none">—</span>;
      },
    },
  ] : []),
  {
    title: 'Floor', key: 'floor', width: 130,
    render: (_: unknown, s: Session) =>
      s.floor ? <span className="att-floor"><EnvironmentOutlined /> {s.floor}</span> : '—',
  },
  { title: 'Branch', key: 'branch', width: 150, render: (_: unknown, s: Session) => s.branch },
  ...(hasCheckOut ? [{
    title: 'Status', key: 'status', width: 120,
    render: (_: unknown, s: Session) => {
      if (s.status === 'inside') return <span className="att-status inside"><span className="att-dot pulse" /> Inside</span>;
      if (s.status === 'out')    return <span className="att-status out"><span className="att-dot" /> Out only</span>;
      return <span className="att-status left"><span className="att-dot" /> Left</span>;
    },
  }] : []),
  {
    title: 'Actions', key: 'actions', width: 110, fixed: 'right' as const,
    render: (_: unknown, s: Session) => (
      <Button
        size="small"
        icon={<EyeOutlined />}
        disabled={!s.attendeeId || !s.date}
        onClick={() => onViewAttendance(s)}
      >
        View
      </Button>
    ),
  },
];

const buildInitialVisible = (columns: { key: string }[]): VisibleColumns =>
  columns.reduce((acc, c) => ({ ...acc, [c.key]: true }), {} as VisibleColumns);

/* ─── Component ──────────────────────────────────────────────────────── */

const AttendancePage = ({ userType, title, userId, extra, showFloorFilter }: AttendancePageProps) => {
  const navigate         = useNavigate();
  const branchId         = useSelector((state: any) => state.branch.selectedBranch);
  const selectedBranchId = typeof branchId === 'object'
    ? (branchId?._id || branchId?.id)
    : branchId;

  const handleViewAttendance = (s: Session) => {
    if (!s.attendeeId || !s.date) return;
    navigate(`${AttendanceDetailRoute}/${s.attendeeId}?date=${s.date}`);
  };

  const [page,           setPage]           = useState(1);
  const [limit,          setLimit]          = useState(50);
  const [search,         setSearch]         = useState('');
  const [statusTab,      setStatusTab]      = useState<string | number>('All');
  const [floorKey,       setFloorKey]       = useState<string | number>('all');
  const [startDate,      setStartDate]      = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [endDate,        setEndDate]        = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [visibleColumns, setVisibleColumns] = useState<VisibleColumns>(buildInitialVisible(buildColumns(true, handleViewAttendance)));

  const activeFloor = showFloorFilter
    ? ATTENDANCE_FLOORS.find(f => f.key === floorKey) ?? ATTENDANCE_FLOORS[0]
    : ATTENDANCE_FLOORS[0]; // 'all', hasCheckOut: true — matches original behaviour when the filter is off

  const { data: attendanceData, isFetching } = useGetAttendanceQuery({
    userType,
    userId,
    startDate,
    endDate,
    branchId: selectedBranchId || undefined,
    page,
    limit,
    search: search || undefined,
    floor: showFloorFilter ? activeFloor.key : undefined,
  });

  const rawData    = (attendanceData as any)?.data || [];
  const totalCount = (attendanceData as any)?.summary?.totalRecords
    ?? (attendanceData as any)?.total
    ?? (attendanceData as any)?.pagination?.total
    ?? 0;

  // Server-computed KPI summary — floor-scoped too, since `floor` is now sent as a query param.
  const summary = (attendanceData as any)?.summary || {};
  const kpi = summary;

  const allSessions = useMemo(() => buildSessions(rawData), [rawData]);

  const dataSource = useMemo(() => {
    if (statusTab === 'Inside') return allSessions.filter(s => s.status === 'inside');
    if (statusTab === 'Left')   return allSessions.filter(s => s.status !== 'inside');
    return allSessions;
  }, [allSessions, statusTab]);

  const handleFloorChange = (key: string | number) => {
    setFloorKey(key);
    const nextFloor = ATTENDANCE_FLOORS.find(f => f.key === key) ?? ATTENDANCE_FLOORS[0];
    setVisibleColumns(buildInitialVisible(buildColumns(nextFloor.hasCheckOut, handleViewAttendance)));
  };

  const handleColumnToggle = (columnKey: string) =>
    setVisibleColumns(prev => ({ ...prev, [columnKey]: !prev[columnKey] }));

  const handleDateRangeChange = (range: { start: Date; end: Date }) => {
    setStartDate(dayjs(range.start).format('YYYY-MM-DD'));
    setEndDate(dayjs(range.end).format('YYYY-MM-DD'));
    setPage(1);
  };

  const handleSearch = (value: string) => { setSearch(value); setPage(1); };

  const allColumns = useMemo(() => buildColumns(activeFloor.hasCheckOut, handleViewAttendance), [activeFloor.hasCheckOut]);
  const columns = useMemo(
    () => allColumns.filter(col => visibleColumns[col.key]),
    [allColumns, visibleColumns],
  );

  return (
    <div className="att-page">

      {/* ── Title ── */}
      <div className="att-titlebar">
        <h2 className="att-title">{title}</h2>
        <span className="att-live">● Live</span>
        {extra && <div className="att-titlebar-extra">{extra}</div>}
      </div>

      {/* ── KPI cards (hidden on a single user's detail attendance) ── */}
      {!userId && (
        <div className="att-stats">
          <StatCard title="Total Records"    value={kpi.totalRecords ?? totalCount}   icon={<ApartmentOutlined />}   color="#8b5cf6" subtitle="in range" />
          <StatCard title="Check-ins"        value={kpi.checkIns ?? 0}                icon={<LoginOutlined />}       color="#10b981" subtitle="in range" />
          {activeFloor.hasCheckOut && (
            <>
              <StatCard title="Check-outs"       value={kpi.checkOuts ?? 0}               icon={<LogoutOutlined />}      color="#f59e0b" subtitle="in range" />
              <StatCard title="Currently Inside" value={kpi.currentlyInside ?? 0}         icon={<TeamOutlined />}        color="#10b981" subtitle="not checked out" />
              <StatCard title="Completed Visits" value={kpi.completedVisits ?? 0}         icon={<CheckCircleOutlined />} color="#6366f1" subtitle="in + out" />
              <StatCard title="Avg Duration"     value={kpi.avgDurationMinutes ? fmtDur(kpi.avgDurationMinutes) : '—'} icon={<ClockCircleOutlined />} color="#06b6d4" subtitle="per visit" />
            </>
          )}
          <StatCard title="Members"          value={kpi.uniqueMembers ?? 0}           icon={<UserOutlined />}        color="#f59e0b" subtitle="unique" />
          <StatCard title="Floors Active"    value={kpi.floorsActive ?? 0}            icon={<EnvironmentOutlined />} color="#ec4899" subtitle="with activity" />
        </div>
      )}

      {/* ── Toolbar ── */}
      <div className="att-header">
        <div className="att-header-left">
          <SearchBar value={search} onChange={handleSearch} placeholder="Search name / mobile..." />
          <DateRangeFilter
            value={{ start: dayjs(startDate).toDate(), end: dayjs(endDate).toDate() }}
            onChange={handleDateRangeChange}
          />
          <Segmented value={statusTab} onChange={setStatusTab} options={['All', 'Inside', 'Left']} />
          {showFloorFilter && (
            <Segmented
              value={floorKey}
              onChange={handleFloorChange}
              options={ATTENDANCE_FLOORS.map(f => ({ label: f.label, value: f.key }))}
            />
          )}
        </div>
        <div className="att-header-right">
          <ColumnVisibility columns={allColumns} visibleColumns={visibleColumns} onColumnToggle={handleColumnToggle} />
        </div>
      </div>

      {/* ── Session table ── */}
      <CommonTable
        columns={columns}
        dataSource={dataSource}
        loading={isFetching}
        rowKey={(record: Session) => record.key}
        scroll={{ x: 1070 }}
        sticky={{ offsetHeader: 0 }}
        className="att-table"
      />

      <CustomPagination
        current={page}
        pageSize={limit}
        total={(attendanceData as any)?.pagination?.total || 0}
        onPageChange={(newPage: number) => setPage(newPage)}
        onPageSizeChange={(newLimit: number) => { setLimit(newLimit); setPage(1); }}
      />
    </div>
  );
};

export default AttendancePage;
